<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">
                {{ model.id ? 'Edit User' : 'Add User' }}
            </div>
        </template>
        <template v-if="!model.id" v-slot:subheader>
            <div v-if="FEATURES.contact_groups">
                Add a new User to your team and optionally assign them to OUs and Groups.
            </div>
            <div v-else>
                Add a new User to your team.
            </div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="model.id" :closeable="false" shadow>
                Editing user <strong class="font-bold">{{ model.email }}</strong>
            </vx-alert>
            <div class="flex flex-col gap-8">
                <div class="flex flex-col gap-4">
                    <h2 class="text-xl font-extrabold m-0">User Details</h2>
                    <div v-if="!model.id">
                        <vx-label>Email</vx-label>
                        <vx-input data-test="user-email" type="email" v-model="model.email" :disabled="model.id" size="lg" :error="displayFirstError(validate.email.$errors)">
                        </vx-input>
                    </div>
                    <div>
                        <vx-label>Role</vx-label>
                        <vx-selectable v-model="model.role"
                            size="lg"
                            relative-dropdown
                            :reduce="option => option.value"
                            :options="teamRolesOptions({
                                exclude: ['owner'],
                            })"
                            :clearable="false"
                            :error="displayFirstError(validate.role.$errors)"
                        >
                        </vx-selectable>
                    </div>
                </div>
                <team-user-org-unit-assignment
                    v-if="FEATURES.contact_groups && !model.id"
                    :team-id="teamId"
                    v-model="assignedOrgUnits"
                    :disabled="limitOfGroupsAndOuReached"
                    ref="orgUnitAssignmentRef"
                />
                <team-user-group-assignment
                    v-if="FEATURES.contact_groups && !model.id"
                    :team-id="teamId"
                    v-model="assignedGroups"
                    :disabled="limitOfGroupsAndOuReached"
                    ref="groupAssignmentRef"
                />
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button @click="updateVisible(false)"
                    type="button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button @click="submit()"
                    data-test="submit-button"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Add' }}
                    <font-awesome-icon widthAuto v-if="!model.id" :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { computed, inject, ref } from 'vue';
import { VxAlert, VxButton, VxInput, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { usersClient, invitationsClient, groupInvitationsClient, orgUnitInvitationsClient } from '~/services';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { teamRolesOptions } from '~/constants/roles';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { helpers, email, maxLength, required } from '@vuelidate/validators';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import TeamUserGroupAssignment from './TeamUserGroupAssignment.vue';
import TeamUserOrgUnitAssignment from './TeamUserOrgUnitAssignment.vue';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

const FEATURES = inject('FEATURES');
const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: false,
    default: () => ({
        email: '',
        role: '',
    })
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate(
    {
        email: {
            maxLength: maxLength(255),
            required: required,
            email: email,
        },
        role: {
            required: required,
            shouldHaveGroups:  helpers.withMessage(
                'Users with the None role should be added to at least one organizational unit or group',
                (value) => {
                    return !FEATURES.contact_groups || value !== 'none' || (assignedGroups.value.length + assignedOrgUnits.value.length) > 0;
                }
            ),
        }
    },
    model,
    { $externalResults }
)

const emit = defineEmits(['invited', 'updated']);
const submitting = ref(false);
const groupAssignmentRef = ref(null);
const orgUnitAssignmentRef = ref(null);
const assignedGroups = ref([]);
const assignedOrgUnits = ref([]);

const MAX_GROUPS_AND_OU = 50;
const limitOfGroupsAndOuReached = computed(() => assignedGroups.value.length + assignedOrgUnits.value.length === MAX_GROUPS_AND_OU)

const updateVisible = (isVisible) => {
    if (!model.value.id) {
        model.value.email = '';
        model.value.role = '';
        assignedGroups.value = [];
        assignedOrgUnits.value = [];
        groupAssignmentRef.value?.reset();
        orgUnitAssignmentRef.value?.reset();
    }

    validate.value.$reset();
    visible.value = isVisible;
}

const submit = async () => {
    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        if (!model.value.id) {
            if (FEATURES.contact_groups && (assignedGroups.value.length + assignedOrgUnits.value.length) > 0) {
                for (const group of assignedGroups.value) {
                    try {
                        await groupInvitationsClient.sendInvitation(props.teamId, group.group_id, {
                            email: model.value.email,
                            role: group.role
                        });
                    } catch (groupError) {
                        const message = `Failed to invite user to group ${group.name}:`;
                        toasted.error(`${message}: ${catchRequestError(groupError)}`);
                    }
                }

                for (const orgUnit of assignedOrgUnits.value) {
                    try {
                        await orgUnitInvitationsClient.sendInvitation(props.teamId, orgUnit.id, {
                            email: model.value.email,
                            role: orgUnit.role
                        });
                    } catch (orgUnitError) {
                        const message = `Failed to invite user to organizational unit ${orgUnit.name}:`;
                        toasted.error(`${message}: ${catchRequestError(orgUnitError)}`);
                    }
                }
            } else {
                await invitationsClient.sendInvite(props.teamId, {
                    // prevent reactivity issues. noticed resetting values in mocks in tests after resetting reactive values when passing without spread.
                    ...model.value
                });
            }

            toasted.success('New User invited successfully')
            emit('invited');
        } else {
            await usersClient.updateUser(props.teamId, model.value.id, model.value);
            emit('updated');
            toasted.success('User updated successfully')
        }
        updateVisible(false);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
}


</script>
